import React, { useState } from "react";
import { Heart } from "lucide-react";
import { useLocalStorage } from "./utils/UseLocalStorage";
import { useDateCalculation } from "./utils/UseDateCalculation";
import { exportData } from "./utils/storage";
import CustomizationSettings from "./utils/CustomizationUtils";
import Setup from "./components/Setup";
import MainCounter from "./components/MainCounter";
import TimeBreakdown from "./components/TimeBreakdown";
import AnniversariesSection from "./components/AnniversariesSection";
import "./App.css";

function App() {
  const [startDate, setStartDate] = useLocalStorage("loveStartDate", "");
  const [partnerNames, setPartnerNames] = useLocalStorage("partnerNames", {
    person1: "",
    person2: "",
  });
  const [anniversaries, setAnniversaries] = useLocalStorage(
    "anniversaries",
    [],
  );
  const [customization, setCustomization] = useLocalStorage("customization", {
    gradient1: "#667eea",
    gradient2: "#764ba2",
    gradient3: "#f093fb",
    font: "#00608D",
    counterImage: null,
  });
  const [showSetup, setShowSetup] = useState(!startDate);
  const [showCustomization, setShowCustomization] = useState(false);

  const { daysTogether, timeUnits } = useDateCalculation(startDate);

  const handleSetupComplete = (data) => {
    if (data.startDate) setStartDate(data.startDate);
    if (data.partnerNames) setPartnerNames(data.partnerNames);
    if (data.anniversaries) setAnniversaries(data.anniversaries);
    setShowSetup(false);
  };

  const handleAddAnniversary = (anniversary) => {
    setAnniversaries([...anniversaries, anniversary]);
  };

  const handleDeleteAnniversary = (id) => {
    setAnniversaries(anniversaries.filter((a) => a.id !== id));
  };

  const handleExport = () => {
    exportData(startDate, partnerNames, anniversaries);
  };

  const handleSaveCustomization = (newSettings) => {
    console.log("Saving customization:", newSettings);
    setCustomization(newSettings);
  };

  // Dynamic gradient style
  const appStyle = {
    background: `linear-gradient(135deg, ${customization.gradient1} 0%, ${customization.gradient2} 50%, ${customization.gradient3} 100%)`,
    minHeight: "100vh",
  };

  const buttonAdd = {
    background: `linear-gradient(135deg, ${customization.gradient1} 0%, ${customization.gradient2} 50%, ${customization.gradient3} 100%)`,
  };
  const counterStyleValue = {
    color: `${customization.font}`,
  };

  const counterStyleLabel = {
    color: `${customization.font}`,
  };

  // Debug log
  //console.log('Current customization:', customization);
  //console.log('App style:', appStyle);

  if (showSetup) {
    return (
      <div className="app" style={appStyle}>
        <Setup
          onComplete={handleSetupComplete}
          initialData={{ startDate, partnerNames }}
        />
      </div>
    );
  }

  return (
    <div className="app" style={appStyle}>
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${10 + Math.random() * 10}s`,
            }}
          />
        ))}
      </div>

      <div className="main-container">
        <div className="content">
          <div className="header">
            <button
              className="settings-btn"
              onClick={() => setShowCustomization(true)}
            >
              🎨 Theme
            </button>
            <button className="settings-btn" onClick={() => setShowSetup(true)}>
              ⚙️ Settings
            </button>
            <button className="export-btn" onClick={handleExport}>
              💾 Export
            </button>
          </div>

          <div className="names-display">
            <span className="name">{partnerNames.person1}</span>
            <Heart className="heart-divider" fill="currentColor" />
            <span className="name">{partnerNames.person2}</span>
          </div>

          <MainCounter
            daysTogether={daysTogether}
            counterStyleLabel={counterStyleLabel}
            counterStyleValue={counterStyleValue}
          />

          <TimeBreakdown timeUnits={timeUnits} />
          <AnniversariesSection
            anniversaries={anniversaries}
            onAdd={handleAddAnniversary}
            onDelete={handleDeleteAnniversary}
            style = {buttonAdd}
          />
        </div>
      </div>

      {showCustomization && (
        <CustomizationSettings
          settings={customization}
          onSave={handleSaveCustomization}
          onClose={() => setShowCustomization(false)}
        />
      )}
    </div>
  );
}

export default App;
